import type { EntityFormConfig, RelationOption } from "./entityForms.types";

/**
 * Every node kind, for the relation types the backend leaves unrestricted (see the "Unrestricted
 * kinds" arm of RelationSyncService.ValidateEndpointTypes). Shared with the Person/Place/Object/
 * Creature dialogs so those lists cannot drift apart from this one again.
 */
export const ALL_ENTITY_TYPES = [
	"Person",
	"Place",
	"Object",
	"Creature",
	"Organization",
	"Event",
	"Species",
	"Concept",
	"Custom"
];

/**
 * Orders a target list by name. The API returns entities in creation order, which stops being
 * scannable past a handful of nodes; case-insensitive so "aang" and "Aang" sort together instead
 * of forming two separate runs.
 */
export const byEntityTitle = <T extends { title: string }>(first: T, second: T): number =>
	first.title.localeCompare(second.title, undefined, { sensitivity: "base" });

/**
 * The relation kinds the backend accepts between any two node kinds — the "Unrestricted kinds" arm
 * of RelationSyncService.ValidateEndpointTypes. Every form spreads this list, so a link can be
 * recorded from whichever end the author is looking at.
 *
 * They used to be hand-copied per form, and the copies drifted: `Influences` reached only the
 * Concept form, which left mentor and teacher edges — the commonest non-family relation in a
 * fictional tree — with no honest representation at all. Both halves of each pair are offered so
 * neither direction forces the author to go and open the other node.
 */
export const UNRESTRICTED_OPTIONS: RelationOption[] = [
	{ value: "AssociatedWith", targetTypes: ALL_ENTITY_TYPES },
	{ value: "Influences", targetTypes: ALL_ENTITY_TYPES },
	{ value: "InfluencedBy", targetTypes: ALL_ENTITY_TYPES },
	{ value: "PartOf", targetTypes: ALL_ENTITY_TYPES },
	{ value: "HasPart", targetTypes: ALL_ENTITY_TYPES },
	{ value: "SymbolOf", targetTypes: ALL_ENTITY_TYPES },
	{ value: "SymbolizedBy", targetTypes: ALL_ENTITY_TYPES }
];

/**
 * Per-type configs for the generic EntityFormDialog. Association options always read
 * "this entity is <type> of the target" (the entity is the From side of the forward edge);
 * targetTypes and canBeEx mirror the backend RelationSyncService validation and RelationTypeRules.
 */
export const organizationFormConfig: EntityFormConfig = {
	entityType: "Organization",
	i18nPrefix: "editor.organizationForm",
	icon: "organization",
	fields: [
		{
			key: "organizationType",
			labelKey: "type",
			suggestions: [
				"Team",
				"House",
				"Order",
				"Guild",
				"Faction",
				"Kingdom",
				"Council",
				"Army",
				"Religion",
				"Cult"
			]
		},
		{ key: "founded", labelKey: "founded" },
		{ key: "region", labelKey: "region" },
		{ key: "motto", labelKey: "motto", fullWidth: true }
	],
	associationOptions: [
		// "Headquartered at" — the organization has its seat at the target place.
		{ value: "HasSeat", targetTypes: ["Place"], canBeEx: true },
		{ value: "LedBy", targetTypes: ["Person"], canBeEx: true },
		{ value: "AlliedWith", targetTypes: ["Person", "Organization", "Place", "Species"], canBeEx: true },
		{ value: "Enemy", targetTypes: ["Person", "Creature", "Organization"], canBeEx: true },
		{ value: "Protects", targetTypes: ["Place", "Person", "Organization"], canBeEx: true },
		{ value: "ProtectedBy", targetTypes: ["Person", "Organization"], canBeEx: true },
		{ value: "MemberOf", targetTypes: ["Organization"], canBeEx: true },
		{ value: "UserOf", targetTypes: ["Object", "Custom"], canBeEx: true },
		...UNRESTRICTED_OPTIONS,
		{ value: "Other", targetTypes: ALL_ENTITY_TYPES, canBeEx: true }
	]
};

export const eventFormConfig: EntityFormConfig = {
	entityType: "Event",
	i18nPrefix: "editor.eventForm",
	icon: "event",
	fields: [
		{
			key: "eventType",
			labelKey: "type",
			suggestions: [
				"Battle",
				"War",
				"Massacre",
				"Wedding",
				"Festival",
				"Treaty",
				"Coronation",
				"Rebellion",
				"Founding"
			]
		},
		{ key: "occurredOn", labelKey: "date" },
		{ key: "participants", labelKey: "participants", fullWidth: true },
		{ key: "outcome", labelKey: "outcome" },
		{ key: "era", labelKey: "era" }
	],
	associationOptions: [
		{ value: "OccurredAt", targetTypes: ["Place"] },
		{ value: "Involved", targetTypes: ["Person", "Organization", "Creature"] },
		{ value: "TriggeredBy", targetTypes: ["Event", "Person", "Organization"] },
		{ value: "ResultedIn", targetTypes: ["Event", "Concept"] },
		{ value: "PartOfEra", targetTypes: ["Event", "Concept"] },
		{ value: "WitnessedBy", targetTypes: ["Person"] },
		...UNRESTRICTED_OPTIONS,
		{ value: "Other", targetTypes: ALL_ENTITY_TYPES, canBeEx: true }
	]
};

export const speciesFormConfig: EntityFormConfig = {
	entityType: "Species",
	i18nPrefix: "editor.speciesForm",
	icon: "species",
	fields: [
		{
			key: "classification",
			labelKey: "classification",
			suggestions: ["Race", "Ancient Race", "Subspecies", "Lineage", "Kind"]
		},
		{ key: "habitat", labelKey: "habitat" },
		{ key: "lifespan", labelKey: "lifespan" },
		{ key: "world", labelKey: "originWorld" },
		{ key: "traits", labelKey: "traits", fullWidth: true },
		{ key: "language", labelKey: "language" }
	],
	associationOptions: [
		{ value: "NativeTo", targetTypes: ["Place"] },
		{ value: "Inhabits", targetTypes: ["Place"] },
		{ value: "OpposedBy", targetTypes: ALL_ENTITY_TYPES },
		{ value: "AncestorOf", targetTypes: ["Species"] },
		{ value: "AlliedWith", targetTypes: ["Person", "Organization", "Place", "Species"], canBeEx: true },
		{ value: "UserOf", targetTypes: ["Object", "Custom"], canBeEx: true },
		...UNRESTRICTED_OPTIONS,
		{ value: "Other", targetTypes: ALL_ENTITY_TYPES, canBeEx: true }
	]
};

export const conceptFormConfig: EntityFormConfig = {
	entityType: "Concept",
	i18nPrefix: "editor.conceptForm",
	icon: "sparkle",
	fields: [
		{
			key: "conceptType",
			labelKey: "category",
			suggestions: ["Magic System", "Religion", "Philosophy", "Prophecy", "Title", "Language", "Tradition", "Law"]
		},
		{ key: "domain", labelKey: "domain" },
		{ key: "source", labelKey: "source" },
		{ key: "symbols", labelKey: "symbols", fullWidth: true },
		{ key: "scope", labelKey: "scope", fullWidth: true },
		{ key: "status", labelKey: "status", suggestions: ["Ancient", "Active", "Fading", "Extinct", "Emerging"] }
	],
	associationOptions: [
		{ value: "PracticedBy", targetTypes: ["Person", "Organization", "Species"], canBeEx: true },
		{ value: "OriginatesFrom", targetTypes: ["Place", "Organization", "Species"] },
		{ value: "OpposedBy", targetTypes: ALL_ENTITY_TYPES },
		// "Linked to", "Influences" and "Symbolized By" now come from UNRESTRICTED_OPTIONS, which also
		// brings the halves this list was missing (Influenced By, Symbol Of, Part Of, Has Part).
		...UNRESTRICTED_OPTIONS,
		{ value: "Other", targetTypes: ALL_ENTITY_TYPES, canBeEx: true }
	]
};

export const customFormConfig: EntityFormConfig = {
	entityType: "Custom",
	i18nPrefix: "editor.customForm",
	icon: "custom",
	fields: [
		{ key: "customTypeLabel", labelKey: "schemaLabel" },
		{ key: "parentGroup", labelKey: "parentGroup" },
		{ key: "world", labelKey: "linkedWorld" },
		{ key: "tags", labelKey: "tags", fullWidth: true }
	],
	associationOptions: [
		// "Related to", "Part Of" and "Symbol Of" now come from UNRESTRICTED_OPTIONS, along with the
		// halves this list was missing (Has Part, Symbolized By, Influences, Influenced By). Kept first
		// so a Custom node still opens on the generic "Associated With", as it did before.
		...UNRESTRICTED_OPTIONS,
		{ value: "UsedBy", targetTypes: ["Person", "Organization", "Species"], canBeEx: true },
		{ value: "StoredAt", targetTypes: ["Place"], canBeEx: true },
		// "Linked world" — the custom node belongs to a world node.
		{ value: "PartOfWorld", targetTypes: ["Place"] },
		{ value: "Other", targetTypes: ALL_ENTITY_TYPES, canBeEx: true }
	]
};

/** Lookup by backend entity type, used to route the Edit action. */
export const entityFormConfigsByType: Record<string, EntityFormConfig> = {
	Concept: conceptFormConfig,
	Custom: customFormConfig,
	Event: eventFormConfig,
	Organization: organizationFormConfig,
	Species: speciesFormConfig
};

/** Lookup by AddEntityDialog option value, used to route the Add flow. */
export const entityFormConfigsByLabel: Record<string, EntityFormConfig> = {
	concept: conceptFormConfig,
	custom: customFormConfig,
	event: eventFormConfig,
	organization: organizationFormConfig,
	species: speciesFormConfig
};

/**
 * Gendered family relation types, mapped to the gender the SUBJECT of the relation sentence normally
 * has ("X is the Father of Y" → X is normally Male). The person form uses this to show explicit
 * direction radios and to suggest a direction from the two people's genders.
 *
 * Across generations, getting the direction wrong flips the tree (a parent recorded below their
 * child). Siblings and spouses do not move a generation, but the word still describes exactly one of
 * the two people — "Brother" says nothing about who is the brother — and nothing on screen said
 * which. They are listed here for that reason, not because they are generational.
 */
export const generationalRelationSubjectGender: Record<string, "Male" | "Female"> = {
	Aunt: "Female",
	Brother: "Male",
	Daughter: "Female",
	Father: "Male",
	Granddaughter: "Female",
	Grandfather: "Male",
	Grandmother: "Female",
	Grandson: "Male",
	Husband: "Male",
	Mother: "Female",
	Nephew: "Male",
	Niece: "Female",
	Sister: "Female",
	Son: "Male",
	Uncle: "Male",
	Wife: "Female"
};

/** True for relation types where the person form shows an explicit direction choice. */
export const isGenerationalRelationType = (relationType: string): boolean =>
	relationType in generationalRelationSubjectGender;
